import { useId } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { useTranslation } from '../../../hooks/useTranslation';

interface CategoryFilterProps {
  categories: string[];
  categoryCounts: Record<string, number>;
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
}

/**
 * Searchable multi-select category filter. Whether picking one filters in
 * the browser or refetches is up to the caller; an empty selection means
 * "all". Scales to many categories (e.g. 50+) where the chip row would not.
 * Renders nothing when there are no categories.
 */
const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  categoryCounts,
  selectedCategories,
  onChange,
}) => {
  const { t } = useTranslation();
  const labelId = useId();

  if (!categories.length) {
    return null;
  }

  // Guard the controlled value: a refetch can drop a selected category before
  // the parent prunes it, which would make the Autocomplete value invalid for
  // one render.
  const safeSelected = selectedCategories.filter(category =>
    categories.includes(category)
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        mb: 2,
      }}
    >
      <Typography id={labelId} variant="body2" sx={{ fontWeight: 600 }}>
        {t('Filter Conversations by Category')}:
      </Typography>
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={categories}
        value={safeSelected}
        onChange={(_event, newValue) => onChange(newValue)}
        // Label (with count) drives both the option list and the search match.
        getOptionLabel={option => `${option} (${categoryCounts[option] ?? 0})`}
        // Selected values render as removable chips (name only, to stay compact).
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} label={option} size="small" {...tagProps} />;
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            placeholder={safeSelected.length ? '' : t('All')}
            inputProps={{ ...params.inputProps, 'aria-labelledby': labelId }}
          />
        )}
        // Match the app's dropdown style (see FilterByAgentDropdown):
        // rounded corners, white background.
        sx={{
          minWidth: 250,
          maxWidth: 560,
          flexGrow: 1,
          bgcolor: '#FFFFFF',
          '& .MuiOutlinedInput-root': { borderRadius: '10px' },
        }}
      />
    </Box>
  );
};

export default CategoryFilter;
